import React, { useState } from 'react';
import vars from '@variables';
import Translate from '@utils/translation-functions';
import useWindowSize from '@hooks/use-window-size';
import { GatsbyImage, getImage } from 'gatsby-plugin-image';
import {
  AuthorBio,
  AuthorName,
  AuthorSection,
  AuthorTitle,
  Avatar,
  Divider,
  UtilityBarContent,
  PublishDate,
  Topic,
  TopicsContainer,
  UtilityBarContainer,
  SelectLanguageContainer,
  SelectBox,
  OptionsBox,
  OptionItem,
  SelectedLanguage,
  ChevronIcon,
  TopicsLanguageContainer,
} from './utility-bar.styles';
import { useDataContext } from '@context/page-context-provider';
import { GlobeIconAtom } from '@atoms/icon-atoms/nav-icon-atoms';
import { navigate } from 'gatsby';
import { GTM_ID } from '../../../../common/constants/gtmIds';

function localeToLanguage(locale: 'en-GB' | 'es' | 'pt') {
  const languageMap = {
    'en-GB': 'English',
    es: 'Español',
    pt: 'Português',
  };
  return languageMap[locale] || locale;
}

function localeToAbbreviation(locale: 'en-GB' | 'es' | 'pt') {
  const abbreviationMap = {
    'en-GB': 'En',
    es: 'Es',
    pt: 'Pt',
  };
  return abbreviationMap[locale] || locale;
}

const UtilityBar: React.FC = () => {
  const { createdAt, locale, author, localeLinks, hidePublishDate, topics } =
    useDataContext().pageContext;
  const avatarImage = author?.image ? getImage(author.image) : null;
  const [isLanguageSelectOpen, setIsLanguageSelectOpen] = useState(false);
  const { width } = useWindowSize();

  const handleLanguageSelect = (to: string) => {
    setIsLanguageSelectOpen(false);
    navigate(to);
  };

  const isTabletOrAbove = !!width && width >= 768;

  const hasAuthor = !!author;
  const hasPublishDate = createdAt && !hidePublishDate;
  const hasTopics = Array.isArray(topics) && topics.length > 0;
  const hasLocaleLinks = localeLinks?.length > 0;

  const showAuthorAndDate = hasAuthor && hasPublishDate;
  const onlyHasTopicsOnMobile =
    hasTopics &&
    !hasAuthor &&
    !hasPublishDate &&
    !hasLocaleLinks &&
    !isTabletOrAbove;
  const utilityBarHidden =
    !hasAuthor &&
    !hasPublishDate &&
    !hasLocaleLinks &&
    (!hasTopics || onlyHasTopicsOnMobile);

  if (utilityBarHidden) return null;

  return (
    <UtilityBarContainer data-testid="utility-bar" aria-label="Utility Bar">
      <UtilityBarContent hidePublishDate={!!hidePublishDate}>
        {hasAuthor && (
          <AuthorSection data-testid="author-section">
            {avatarImage && (
              <Avatar data-testid="avatar">
                <GatsbyImage image={avatarImage} alt={author.name} />
              </Avatar>
            )}
            <AuthorBio data-testid="author-bio">
              <AuthorName data-testid="author-name">{author.name}</AuthorName>
              {author.title && (
                <AuthorTitle data-testid="author-title">
                  {author.title}
                </AuthorTitle>
              )}
            </AuthorBio>
          </AuthorSection>
        )}
        {showAuthorAndDate && <Divider />}
        {hasPublishDate && (
          <PublishDate data-testid="publish-date">
            <span>{Translate('Published on', true)}</span>{' '}
            <time dateTime={createdAt}>
              {Intl.DateTimeFormat(locale, {
                dateStyle: 'long',
              }).format(new Date(createdAt))}
            </time>
          </PublishDate>
        )}
        <TopicsLanguageContainer alignLeft={!hasAuthor && !hasPublishDate}>
          {hasTopics && (
            <TopicsContainer
              data-testid="topics-container"
              role="region"
              aria-label="Related topics"
            >
              {topics.map((topic, index) => (
                <Topic
                  data-testid={`topic-${index}`}
                  href={`/${Translate('exploreLink', true)}?topics=${topic.title}`}
                  key={`${topic.title}-${index}`}
                  data-gtm-id={GTM_ID.PILLS_SELECTOR}
                  rel="noreferrer"
                >
                  {Translate(topic.title, true)}
                </Topic>
              ))}
            </TopicsContainer>
          )}
          {hasTopics && hasLocaleLinks && <Divider />}
          {hasLocaleLinks && (
            <SelectLanguageContainer
              role="combobox"
              aria-haspopup="listbox"
              aria-expanded={isLanguageSelectOpen}
              aria-label="Language Selector"
            >
              <SelectBox
                role="button"
                aria-controls="language-listbox"
                aria-expanded={isLanguageSelectOpen}
                aria-label="Selected language"
                onClick={() => setIsLanguageSelectOpen(!isLanguageSelectOpen)}
              >
                <GlobeIconAtom
                  width={24}
                  height={24}
                  fill={vars.coreDarkGrey}
                />
                <SelectedLanguage>
                  {isTabletOrAbove
                    ? localeToAbbreviation(locale)
                    : localeToLanguage(locale)}
                </SelectedLanguage>
                <ChevronIcon isOpen={isLanguageSelectOpen} />
              </SelectBox>
              <OptionsBox
                id="language-listbox"
                role="listbox"
                isOpen={isLanguageSelectOpen}
                aria-label="Available languages"
              >
                {localeLinks.map(link => (
                  <OptionItem
                    key={link.url}
                    onClick={() => handleLanguageSelect(link.url)}
                  >
                    {link.title}
                  </OptionItem>
                ))}
              </OptionsBox>
            </SelectLanguageContainer>
          )}
        </TopicsLanguageContainer>
      </UtilityBarContent>
    </UtilityBarContainer>
  );
};

export default UtilityBar;
